import _ from "lodash";

import { BASE_URL } from "config/urls";
import { deleteCall, get, post, useFetch, usePaginatedFetch } from "utils/sdk";
import { formatGetParams } from "utils/urls";

import { IOpenAICustomPromptResponse } from "entities/AISummaries/sdk";
import { IPackageDetailsField } from "entities/Checklist/sdk";
import {
  EAITablesDisplayMode,
  EMemoSectionType,
  EMemoSourcesAndUsesSectionDisplayMode,
} from "entities/MemoTemplate/constants";

import { ERiskMoveDirection } from "./components/Sections/RisksAndMitigants/constants";

export enum ProformaPDFFileStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum MemoPDFStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export interface IMemo {
  name: string;
  additional_header_info: string;
  sections: IMemoSection[];
  ai_tables_display_mode: EAITablesDisplayMode;
  include_proforma_pdf: boolean;
}

export interface IProformaPDF {
  id: number;
  status: ProformaPDFFileStatus;
  file?: { signed_url: string; original_file_name: string };
}

export interface IMemoSectionExcelTableFieldValue {
  order: number;
  columns: Array<string>;
}

export interface IMemoOpenAIResponse {
  openai_custom_prompt_response:
    | (IOpenAICustomPromptResponse & {
        prompt_id: number;
        openai_document_assistant_id: number;
      })
    | null;
  response: string;
  updated_at: string | null;
}

export interface AIPromptSection {
  source_documents: Array<ISourceDocument>;
}

export interface IMemoSection {
  section_id: number;
  order: number;
  section_type: EMemoSectionType;
  title: string;
  comment_count: number;
  break_before: boolean;
  limit_image_section_height: boolean;
  table_section_compact_view: boolean;
  sources_and_uses_section_display_mode: EMemoSourcesAndUsesSectionDisplayMode;
  amenities_section_display_image: boolean;
  is_na: boolean;
  memo_section_openai_responses: Array<IMemoOpenAIResponse>;
  checklist_table_id: number;
  checklist_table_updated_at: string;
  checklist_table: Array<Array<{ value: string; is_editable: boolean }>>;
  ai_prompt: AIPromptSection | null;
  is_prompt_response_in_progress: boolean;
  fields?: IPackageDetailsField[];
  risks: Array<{
    id: number;
    title: string;
    description: string;
    template: {
      title: string;
      description: string;
    } | null;
  }>;
}

export interface IMemoSectionComment {
  id: number | string;
  created_by: {
    user_id: number;
    first_name: string;
    last_name: string;
    company_name: string;
    avatar: string | null;
  };
  created_at: string;
  message: string;
  seen_by: Array<{
    first_name: string;
    last_name: string;
    user_id: number;
  }>;
}

interface IShareableMemoLink {
  id: number;
  url: string;
}

export interface IDealTeamCompany {
  name: string;
  locations: Array<{
    street: string;
    city: string;
    state: string;
    zipcode: string;
  }>;
  established_in: string;
  website_url: string;
  description: string;
  executive_summary: Array<{
    summary: string;
    created_at: string;
    updated_at: string;
  }>;
}

export interface IDealSummary {
  summary: string;
  updated_at: string | null;
}

export const useMemoDealDetails = ({
  memoId,
  dealId,
}: {
  memoId: number;
  dealId: number;
}) =>
  useFetch<IMemo>(
    !_.isNaN(dealId) && !_.isNaN(memoId)
      ? `v2/memo/memo/${memoId}/deal/${dealId}/details/`
      : undefined
  );

export const useProformaPDFRetrieve = ({ dealId }: { dealId: number }) =>
  useFetch<IProformaPDF>(`v2/deal/${dealId}/proforma-pdf/retrieve/`);

export const updateMemoDealInfo = ({
  memoId,
  dealId,
  additionalHeaderInfo,
}: {
  memoId: number;
  dealId: number;
  additionalHeaderInfo: string;
}) =>
  post<{ additional_header_info: string }>(
    `${BASE_URL}v2/memo/memo/${memoId}/deal/${dealId}/memo-details/update/`,
    { additional_header_info: additionalHeaderInfo }
  );

export const useShareableMemoLink = ({
  dealId,
  memoId,
  enabled,
}: {
  dealId: number;
  memoId: number;
  enabled: boolean;
}) =>
  useFetch<IShareableMemoLink>(
    enabled
      ? `v2/deal/deal/${dealId}/memo/${memoId}/get-share-token/`
      : undefined
  );

export const createShareableMemoLink = (dealId: number, memoId: number) =>
  post<IShareableMemoLink>(
    `${BASE_URL}v2/deal/deal/${dealId}/memo/${memoId}/create-share-token/`
  );

export const expireShareableMemoLink = (dealId: number, memoId: number) =>
  post<{ response?: string }>(
    `${BASE_URL}v2/deal/deal/${dealId}/expire-share-token/`,
    {
      memo_id: memoId,
    }
  );

export const addMemoSectionComment = ({
  dealId,
  sectionId,
  message,
}: {
  dealId: number;
  sectionId: number;
  message: string;
}): Promise<IMemoSectionComment> =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${sectionId}/memo-section-comment/create/`,
    { message }
  );

export const useSectionCommentsList = ({
  dealId,
  memoSectionId,
}: {
  dealId?: number;
  memoSectionId?: number;
}) =>
  useFetch<IMemoSectionComment[]>(
    dealId && memoSectionId
      ? `v2/memo/deal/${dealId}/memo-section/${memoSectionId}/memo-section-comment/list/`
      : undefined
  );

export const markSectionCommentsListAsSeen = ({
  memoSectionId,
  dealId,
}: {
  memoSectionId?: number;
  dealId?: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/memo-section-comment/mark-as-seen/`
  );

export const useDealTeamGroup = ({
  dealId,
  groupName,
}: {
  dealId?: number;
  groupName: string;
}) =>
  useFetch<IDealTeamCompany[]>(
    dealId && groupName
      ? `v2/deal/deal/${dealId}/team-group/?group_name=${groupName}`
      : undefined
  );

export const useDealSummaryByCategory = ({
  dealId,
  category,
}: {
  dealId?: number;
  category: string;
}) =>
  useFetch<IDealSummary>(
    dealId && category
      ? `v2/deal/deal/${dealId}/category/${category}/summary/`
      : undefined
  );

export const updateSummary = ({
  dealId,
  category,
  data,
}: {
  dealId: number;
  category: string;
  data: {
    summary: string;
  };
}) =>
  post(
    `${BASE_URL}v2/deal/deal/${dealId}/category/${category}/summary/update/`,
    data
  );

export const memoSectionNAStatusUpdate = ({
  dealId,
  memoSectionId,
  naStatus,
}: {
  dealId: number;
  memoSectionId: number;
  naStatus: boolean;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/na-status/update/`,
    { is_na: naStatus }
  );

export const memoSectionAISummaryResponseCreateOrUpdate = ({
  dealId,
  memoSectionId,
  openAICustomPromptResponseId,
  response,
}: {
  dealId: number;
  memoSectionId: number;
  openAICustomPromptResponseId: number | null;
  response: string;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/openai-response/create-or-update/`,
    { openai_custom_prompt_response_id: openAICustomPromptResponseId, response }
  );

export const memoSectionAISummaryResponseDelete = ({
  dealId,
  memoSectionId,
}: {
  dealId: number;
  memoSectionId: number;
}) =>
  deleteCall(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/openai-response/delete/`
  );

export interface ISourceDocument {
  id: number;
  name: string;
  files: Array<{
    id: number;
    original_file_name: string;
  }>;
  is_missing: boolean;
  is_na: boolean;
}

export const useSourceDocumentsForMemo = ({
  memoId,
  dealId,
  enabled = true,
}: {
  memoId: number;
  dealId: number;
  enabled?: boolean;
}) =>
  useFetch<Array<ISourceDocument>>(
    enabled
      ? `v2/memo/deal/${dealId}/memo/${memoId}/source-document/list/`
      : undefined
  );

export interface IAIAnswerMemoSection {
  id: number;
  title: string;
  is_na: boolean;
  source_documents_count: number;
  ai_prompt: {
    title: string;
    id: number;
    is_response_in_progress: boolean;
  };
  due_diligence_section: {
    field: Array<{ name: string; id: number; value: string }>;
  };
}

export interface ISourceDocumentDetails {
  id: number;
  name: string;
  is_na: boolean;
  files: Array<{
    id: number;
    original_file_name: string;
    file_name: string;
    file_type: string;
    is_file_value_selected_for_ai_data_extraction: boolean;
    custom_field_value_id: number;
  }>;

  memo_sections: Array<IAIAnswerMemoSection>;
}

export const useSourceDocumentRetrieve = ({
  memoId,
  sourceDocumentId,
  dealId,
  enabled,
}: {
  memoId: number;
  sourceDocumentId: number;
  dealId: number;
  enabled: boolean;
}) =>
  useFetch<ISourceDocumentDetails>(
    enabled
      ? `v2/memo/deal/${dealId}/memo/${memoId}/source-document/${sourceDocumentId}/retrieve/`
      : undefined
  );

export interface MemoSectionAIPromptResponse {
  id: number;
  value: string;
}

export const memoSectionAIProcessTrigger = ({
  memoId,
  dealId,
  memoSectionIds,
}: {
  memoId: number;
  dealId: number;
  memoSectionIds: Array<number>;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/memo-section-ai-process/trigger/`,
    {
      memo_section_ids: memoSectionIds,
    }
  );

export const sourceDocumentFileSignedUrlRetrieve = ({
  dealId,
  fileId,
  sourceDocumentId,
}: {
  fileId: number;
  dealId: number;
  sourceDocumentId: number;
}): Promise<string> =>
  get(
    `${BASE_URL}v2/memo/deal/${dealId}/source-document/${sourceDocumentId}/file/${fileId}/signed-url/retrieve/`
  );

export interface IMemoRiskTemplate {
  id: number;
  title: string;
}

export const useMemoRiskTemplates = ({
  dealId,
  enabled = true,
}: {
  dealId: number;
  enabled?: boolean;
}) =>
  useFetch<Array<IMemoRiskTemplate>>(
    enabled ? `v2/memo/deal/${dealId}/risk-template/list/` : undefined
  );

export interface IMemoRisk {
  id: number;
  title: string;
  description: string;
  template: {
    title: string;
    description: string;
  } | null;
}

export const riskAdd = ({
  dealId,
  memoSectionId,
  riskTemplateId,
}: {
  dealId: number;
  memoSectionId: number;
  riskTemplateId?: number;
}) =>
  post<IMemoRisk>(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/add/`,
    {
      risk_template_id: riskTemplateId,
    }
  );

export const riskUpdate = ({
  dealId,
  memoSectionId,
  title,
  description,
  riskId,
}: {
  dealId: number;
  memoSectionId: number;
  title: string;
  description: string;
  riskId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/${riskId}/update/`,
    { title, description }
  );

export const riskDelete = ({
  dealId,
  memoSectionId,
  riskId,
}: {
  dealId: number;
  memoSectionId: number;
  riskId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/${riskId}/delete/`
  );

export const riskMove = ({
  dealId,
  memoSectionId,
  riskId,
  direction,
}: {
  dealId: number;
  memoSectionId: number;
  riskId: number;
  direction: ERiskMoveDirection;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo-section/${memoSectionId}/risk/${riskId}/move/`,
    {
      direction,
    }
  );

export const memoPDFCreate = ({
  dealId,
  memoId,
  html,
}: {
  dealId: number;
  memoId: number;
  html: string;
}) =>
  post(`${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/pdf/create/`, {
    html,
  });

export const memoPDFDelete = ({
  dealId,
  memoId,
  memoPDFId,
}: {
  dealId: number;
  memoId: number;
  memoPDFId: number;
}) =>
  post(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/pdf/${memoPDFId}/delete/`
  );

export const memoPDFDataWithSignedUrlRetrieve = ({
  dealId,
  memoId,
  memoPDFId,
}: {
  dealId: number;
  memoId: number;
  memoPDFId: number;
}) =>
  get<{ original_file_name: string; url: string }>(
    `${BASE_URL}v2/memo/deal/${dealId}/memo/${memoId}/pdf/${memoPDFId}/signed-url-and-original-file-name/retrieve/`
  );

export interface IMemoPDFDocument {
  id: number;
  status: MemoPDFStatus;
  created_at: string;
  created_by: { first_name: string; last_name: string };
  file: { id: number; original_file_name: string };
  is_created_through_shareable_link: boolean;
}

export const useMemoPDFList = ({
  dealId,
  memoId,
  queryParams,
}: {
  dealId: number;
  memoId: number;
  enabled?: boolean;
  queryParams?: { [key: string]: string };
}) =>
  usePaginatedFetch<IMemoPDFDocument>({
    url: `v2/memo/deal/${dealId}/memo/${memoId}/pdf/list/${formatGetParams(
      queryParams || {}
    )}`,
    params: {
      limit: 15,
    },
    options: {
      dedupingInterval: 0,
    },
  });
